/**
 * Shared Solana wallet helpers for sign/send normalization.
 * Used by hooks, services, and pages so behavior stays consistent.
 */

const BASE58_SIGNATURE_REGEX = /^[1-9A-HJ-NP-Za-km-z]{64,128}$/;

/** Use when measuring or logging unsigned v0 transactions (default serialize() asserts). */
export const UNSIGNED_V0_TX_SERIALIZE_OPTS = {
  requireAllSignatures: false,
  verifySignatures: false,
};

export function serializeUnsignedTransaction(tx) {
  if (!tx || typeof tx.serialize !== 'function') return null;
  return tx.serialize(UNSIGNED_V0_TX_SERIALIZE_OPTS);
}

/** True when value is a non-empty base58 Solana transaction signature. */
export function isValidSolanaTxSignature(value) {
  if (typeof value !== 'string') return false;
  const normalized = value.trim();
  return normalized.length > 0 && BASE58_SIGNATURE_REGEX.test(normalized);
}

/**
 * Extract base58 tx signature from wallet `signAndSendTransaction` results.
 * Handles plain strings, JSON strings, and wallet response objects.
 */
export function normalizeSignAndSendSignature(out) {
  if (out == null) return '';
  if (typeof out === 'string') {
    const trimmed = out.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        return normalizeSignAndSendSignature(parsed);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  if (typeof out === 'object') {
    const s =
      out.signature ?? out.txid ?? out.transactionSignature ?? out.txSignature;
    if (s != null && s !== out) {
      return typeof s === 'string' ? s.trim() : String(s);
    }
    const nested =
      out.result ??
      out.data ??
      out.response ??
      out.payload;
    if (nested != null && nested !== out) {
      return normalizeSignAndSendSignature(nested);
    }
  }
  return String(out);
}

/** Normalize wallet send response and enforce a valid Solana signature string. */
export function extractAndValidateTransactionSignature(result) {
  const normalized = normalizeSignAndSendSignature(result)?.trim();
  if (!normalized || normalized.includes('[object')) {
    throw new Error('Wallet did not return a valid transaction signature');
  }
  if (!BASE58_SIGNATURE_REGEX.test(normalized)) {
    throw new Error(`Wallet returned non-base58 signature: ${normalized}`);
  }
  return normalized;
}

/** @param {import("@solana/web3.js").Connection} connection @param {string} signature @param {import("@solana/web3.js").Commitment} [commitment] @param {number} [maxMs] */
async function pollSignatureUntilConfirmed(connection, signature, commitment = "confirmed", maxMs = 90_000) {
  const started = Date.now();
  let waitMs = 5_000;
  while (Date.now() - started < maxMs) {
    try {
      const status = await connection.getSignatureStatus(signature);
      const confirmationStatus = status?.value?.confirmationStatus;
      if (confirmationStatus === "confirmed" || confirmationStatus === "finalized") {
        if (status?.value?.err) {
          throw new Error("Transaction failed on-chain.");
        }
        return signature;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes("429")) {
        await new Promise((resolve) => setTimeout(resolve, waitMs));
        waitMs = Math.min(waitMs * 2, 15_000);
        continue;
      }
      throw err;
    }
    await new Promise((resolve) => setTimeout(resolve, waitMs));
  }
  throw new Error(
    "Transaction confirmation timed out. Check Solscan or your wallet, then refresh.",
  );
}

/**
 * Confirm a transaction using HTTP polling (no WebSocket subscriptions).
 * Handles blockhash-expired races by re-checking signature status.
 */
export async function confirmTransactionWithStatusFallback(
  connection,
  signatureOrResult,
  options = {}
) {
  const signature = extractAndValidateTransactionSignature(signatureOrResult);
  const { commitment = 'confirmed' } = options;

  const status = await connection.getSignatureStatus(signature);
  if (
    status?.value?.confirmationStatus === 'confirmed' ||
    status?.value?.confirmationStatus === 'finalized'
  ) {
    if (status?.value?.err) {
      throw new Error("Transaction failed on-chain.");
    }
    return signature;
  }

  return pollSignatureUntilConfirmed(connection, signature, commitment);
}

/**
 * Confirm a transaction landed successfully (not just submitted).
 * @param {import("@solana/web3.js").Connection} connection
 * @param {unknown} signatureOrResult
 * @param {Parameters<typeof confirmTransactionWithStatusFallback>[2]} [options]
 */
export async function assertTransactionSucceeded(
  connection,
  signatureOrResult,
  options = {},
) {
  return confirmTransactionWithStatusFallback(connection, signatureOrResult, options);
}

const DEFAULT_CONFIRM_TIMEOUT_MS = 90_000;

/**
 * Confirm a transaction with a wall-clock timeout (avoids stuck withdraw UI).
 * @param {import("@solana/web3.js").Connection} connection
 * @param {unknown} signatureOrResult
 * @param {Parameters<typeof confirmTransactionWithStatusFallback>[2]} [options]
 * @param {number} [timeoutMs]
 */
export async function assertTransactionSucceededWithTimeout(
  connection,
  signatureOrResult,
  options = {},
  timeoutMs = DEFAULT_CONFIRM_TIMEOUT_MS,
) {
  let timer;
  try {
    return await Promise.race([
      assertTransactionSucceeded(connection, signatureOrResult, options),
      new Promise((_, reject) => {
        timer = setTimeout(
          () =>
            reject(
              new Error(
                "Transaction confirmation timed out. Check Solscan or your wallet, then refresh.",
              ),
            ),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** @param {unknown} error */
function walletErrorMessage(error) {
  if (!error) return "";
  if (typeof error === "object") {
    if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
    if ("reason" in error && typeof error.reason === "string") {
      return error.reason;
    }
    const nested = /** @type {{ error?: { message?: string } }} */ (error).error;
    if (nested && typeof nested.message === "string") {
      return nested.message;
    }
  }
  return String(error);
}

/** Simulation / send failed because the wallet cannot cover rent or fees. */
export function isInsufficientFundsWalletError(error) {
  if (!error) return false;
  const lower = walletErrorMessage(error).toLowerCase();
  return (
    lower.includes("insufficient funds") ||
    lower.includes("insufficient sol") ||
    lower.includes("not enough sol") ||
    lower.includes("insufficient lamports") ||
    lower.includes("insufficient balance") ||
    (lower.includes("simulation failed") &&
      (lower.includes("insufficient") ||
        lower.includes("0x1") ||
        lower.includes("custom program error")))
  );
}

/** User dismissed the wallet prompt — do not retry or fall back to another signing path. */
export function isUserRejectedWalletError(error) {
  if (!error) return false;
  if (isInsufficientFundsWalletError(error)) return false;

  const code = error.code;
  if (code === 4001 || code === 4900) return true;

  const msg = walletErrorMessage(error).toLowerCase();
  if (
    msg.includes('user rejected') ||
    msg.includes('user denied') ||
    msg.includes('user cancelled') ||
    msg.includes('user canceled') ||
    msg.includes('rejected the request') ||
    msg.includes('request rejected') ||
    msg.includes('transaction was rejected') ||
    msg.includes('rejected the transaction') ||
    msg.includes('denied transaction') ||
    msg.includes('approval denied') ||
    msg.includes('cancelled by user') ||
    msg.includes('canceled by user')
  ) {
    return true;
  }
  if (
    msg.includes('reject') &&
    (msg.includes('sign') || msg.includes('signature') || msg.includes('wallet'))
  ) {
    return true;
  }

  const name = error instanceof Error ? error.name : "";
  if (name === "WalletSignTransactionError" || name === "WalletSendTransactionError") {
    return !msg || msg === "[object object]";
  }

  return false;
}

/** Phantom / wallet security blocked the request (distinct from user rejection). */
export function isPhantomBlockedError(error) {
  if (!error || isUserRejectedWalletError(error)) return false;
  const msg = walletErrorMessage(error).toLowerCase();
  return (
    msg.includes("request blocked") ||
    msg.includes("blocked by phantom") ||
    msg.includes("transaction blocked") ||
    msg.includes("security risk") ||
    msg.includes("malicious")
  );
}

/** Phantom / wallet-standard: site origin is not authorized to sign. */
export const WALLET_NOT_CONNECTED_MESSAGE =
  "Wallet not connected. Please connect your wallet first.";

export function isWalletNotConnectedError(error) {
  if (!error || isUserRejectedWalletError(error)) return false;
  const nested = typeof error === "object" ? error.error ?? error.cause : undefined;
  const code =
    typeof error === "object" ? error.code ?? nested?.code : undefined;
  if (code === 4100) return true;

  const msg = walletErrorMessage(error).toLowerCase();
  return (
    msg.includes("site not connected") ||
    msg.includes("wallet not connected") ||
    msg.includes("not been authorized")
  );
}

/** Friendly copy when the user dismisses a wallet signing prompt. */
export function formatLaunchUserRejectedMessage(actionLabel = "Launch coin") {
  return `You rejected the transaction in your wallet. Press ${actionLabel} again when you're ready.`;
}

/**
 * @param {unknown} error
 * @param {{ actionLabel?: string; fallbackMessage?: string }} [options]
 */
export function formatLaunchErrorMessage(
  error,
  { actionLabel = "Launch coin", fallbackMessage = "Launch failed." } = {},
) {
  if (isUserRejectedWalletError(error)) {
    return formatLaunchUserRejectedMessage(actionLabel);
  }
  if (isWalletNotConnectedError(error)) {
    return WALLET_NOT_CONNECTED_MESSAGE;
  }
  if (error instanceof Error && error.message?.trim()) {
    return error.message.trim();
  }
  return fallbackMessage;
}

/**
 * Pick injected `signAndSendTransaction` provider for the wallet the user actually connected.
 * Falls back to common injectors only when the connected wallet name is unknown.
 */
export function resolveSignAndSendProvider(connectedWalletName) {
  if (typeof window === 'undefined') return null;

  const name = String(connectedWalletName || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  if (name.includes('phantom')) return window.phantom?.solana || null;
  if (name.includes('solflare')) return window.solflare || null;
  if (name.includes('backpack')) return window.backpack?.solana || null;
  if (name.includes('coinbase')) return window.coinbaseSolana || null;
  if (name.includes('brave')) return window.braveSolana || null;
  if (name.includes('jupiter')) return window.jupiter?.solana || null;
  if (name.includes('idx')) {
    return (window.solana?.isIDXWallet && window.solana) || window.idxWallet || null;
  }

  return window.phantom?.solana || window.solflare || window.solana || null;
}

function normalizeWalletProviderId(value) {
  return String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Resolve an injected provider that exposes `signTransaction` for the connected wallet id/name.
 * @param {string | null | undefined} providerId
 * @returns {import("@solana/wallet-adapter-base").WalletAdapter | null}
 */
export function resolveInjectedSignProvider(providerId) {
  if (typeof window === "undefined") return null;

  const name = normalizeWalletProviderId(providerId);
  /** @type {unknown[]} */
  const candidates = [];

  if (name.includes("phantom")) {
    candidates.push(window.phantom?.solana);
    if (window.solana?.isPhantom) candidates.push(window.solana);
  } else if (name.includes("solflare")) {
    candidates.push(window.solflare);
  } else if (name.includes("backpack")) {
    candidates.push(window.backpack?.solana, window.backpack);
  } else if (name.includes("coinbase")) {
    candidates.push(window.coinbaseSolana);
  } else if (name.includes("brave")) {
    candidates.push(window.braveSolana);
  } else if (name.includes("jupiter")) {
    candidates.push(window.jupiter?.solana);
  } else if (name.includes("trust")) {
    candidates.push(window.trustwallet?.solana);
    if (window.solana?.isTrust) candidates.push(window.solana);
  } else if (name.includes("idx")) {
    if (window.solana?.isIDXWallet) candidates.push(window.solana);
    candidates.push(window.IdxWallet, window.idxWallet);
  } else if (name) {
    const fallback = resolveSignAndSendProvider(name);
    if (fallback) candidates.push(fallback);
  }

  candidates.push(
    window.phantom?.solana,
    window.solflare,
    window.solana?.isIDXWallet ? null : window.solana,
  );

  for (const candidate of candidates) {
    const provider = /** @type {{ signTransaction?: (tx: unknown) => Promise<unknown> } | null} */ (
      candidate
    );
    if (provider?.signTransaction) return provider;
  }

  return null;
}

/**
 * Resolve `signTransaction` from wallet-adapter context, connected adapter instances,
 * or injected browser wallets (Phantom, Solflare, MetaMask, etc.).
 */
export function resolveWalletSignTransactionFn({
  isSolanaAdapterConnected,
  adapterSignTransaction,
  adapterWallets,
  directProviderId,
  connectedWalletName,
  getMetaMaskSolanaDirectProvider,
}) {
  if (isSolanaAdapterConnected && typeof adapterSignTransaction === "function") {
    return adapterSignTransaction;
  }

  for (const w of adapterWallets ?? []) {
    const adapter = w?.adapter;
    if (adapter?.connected && typeof adapter.signTransaction === "function") {
      return (tx) => adapter.signTransaction(tx);
    }
  }

  if (typeof getMetaMaskSolanaDirectProvider === "function") {
    const metamask = getMetaMaskSolanaDirectProvider();
    if (metamask?.signTransaction) {
      return (tx) => metamask.signTransaction(tx);
    }
  }

  const injected = resolveInjectedSignProvider(
    directProviderId || connectedWalletName,
  );
  if (injected?.signTransaction) {
    return (tx) => injected.signTransaction(tx);
  }

  return null;
}

/**
 * Resolve `signAllTransactions` from wallet-adapter context, connected adapter instances,
 * or injected browser wallets (Phantom batch approval UI).
 */
export function resolveWalletSignAllTransactionsFn({
  isSolanaAdapterConnected,
  adapterSignAllTransactions,
  adapterWallets,
  directProviderId,
  connectedWalletName,
  getMetaMaskSolanaDirectProvider,
}) {
  if (isSolanaAdapterConnected && typeof adapterSignAllTransactions === "function") {
    return adapterSignAllTransactions;
  }

  for (const w of adapterWallets ?? []) {
    const adapter = w?.adapter;
    if (adapter?.connected && typeof adapter.signAllTransactions === "function") {
      return (txs) => adapter.signAllTransactions(txs);
    }
  }

  if (typeof getMetaMaskSolanaDirectProvider === "function") {
    const metamask = getMetaMaskSolanaDirectProvider();
    if (metamask?.signAllTransactions) {
      return (txs) => metamask.signAllTransactions(txs);
    }
  }

  const injected = resolveInjectedSignProvider(directProviderId || connectedWalletName);
  if (injected?.signAllTransactions) {
    return (txs) => injected.signAllTransactions(txs);
  }

  return null;
}
